import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { validateEmail } from '../../utils/emailValidation';

type VerifyStatus = 'verifying' | 'success' | 'alreadyVerified' | 'failed';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [message, setMessage] = useState('');

  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState('');
  const [resendInfo, setResendInfo] = useState('');
  const [isResending, setIsResending] = useState(false);

  const hasCalledRef = useRef(false);

  // Fix JWT token URL encoding (spaces → +)
  const fixTokenFromUrl = (token: string) => token.replace(/\s/g, '+');

  useEffect(() => {
    if (hasCalledRef.current) return;
    hasCalledRef.current = true;

    const rawToken = searchParams.get('token');
    if (!rawToken) {
      setStatus('failed');
      setMessage('Verification link is missing or invalid.');
      return;
    }

    const token = fixTokenFromUrl(rawToken);

    (async () => {
      try {
        const response = await apiService.verifyEmail(token);
        if (response.success) {
          const backendMessage = (response.message || '').toLowerCase();
          const isAlreadyVerified = backendMessage.includes('already');
          setStatus(isAlreadyVerified ? 'alreadyVerified' : 'success');
          setMessage(response.message || '');
          if (!isAlreadyVerified) {
            setTimeout(() => navigate('/login'), 3000);
          }
        } else {
          setStatus('failed');
          setMessage(response.error || 'This verification link is invalid or has expired.');
        }
      } catch (error) {
        setStatus('failed');
        setMessage(error instanceof Error ? error.message : 'Unable to verify email. Please try again.');
      }
    })();
  }, [searchParams, navigate]);

  const handleResend = async () => {
    setResendError('');
    setResendInfo('');

    const trimmed = resendEmail.trim();
    // Format only — a user resending verification may already have an
    // account on a domain we'd otherwise flag, and the response is generic
    // either way, so don't hard-block here.
    const validation = validateEmail(trimmed, { blockDisposable: false, checkTypo: false });
    if (!validation.ok) {
      setResendError(validation.error || 'Please enter a valid email address.');
      return;
    }

    setIsResending(true);
    try {
      const response = await apiService.resendEmailVerification(trimmed);
      if (response.success) {
        setResendInfo(response.message || 'Verification email sent. Please check your inbox.');
      } else {
        setResendError(response.error || 'Unable to resend verification email. Please try again.');
      }
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Unable to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen h-full flex items-center justify-center relative">
      <picture className="absolute inset-0 z-0">
        <source media="(max-width: 480px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-mobile.webp`} />
        <source media="(max-width: 920px)" srcSet={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-tablet.webp`} />
        <img
          src={`${process.env.PUBLIC_URL}/SVGIcons/login-bg-desktop.webp`}
          alt=""
          className="w-full h-full object-cover"
        />
      </picture>

      <div className="relative z-10 w-full max-w-md mx-4 bg-[var(--modal-background)] rounded-lg shadow-xl p-8 text-center zoomOutLevel">
        {status === 'verifying' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
            </div>
            <h3 className="text-xl font-semibold text-[var(--color-text)] mb-2">Verifying your email…</h3>
            <p className="text-base font-light text-[var(--color-sub-title-text)]">Please wait while we confirm your verification link.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <CheckCircle2 className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">
              You&apos;re all set!
            </h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">
              Your email has been verified. You can now sign in and start using your SmartEco account.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="add-device-save-btn"
            >
              Sign In
            </button>
          </>
        )}

        {status === 'alreadyVerified' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-b from-[#0F796B] to-[#6FDC95]">
              <CheckCircle2 className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">
              Email already verified
            </h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">
              This email is already verified. Please sign in to continue to your SmartEco account.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="add-device-save-btn"
            >
              Sign In
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-gradient-to-r from-[#d92b1e] to-[#b04a43]">
              <AlertCircle className="h-10 w-10 text-[var(--modal-background)]" />
            </div>
            <h3 className="sm:text-2xl text-xl font-light text-[var(--color-text)] mb-1">Verification Failed</h3>
            <p className="text-[var(--color-sub-title-text)] font-normal sm:text-sm text-xs mb-4">{message}</p>

            <div className="text-left">
              <label className="form-label" htmlFor="resendEmail">Email</label>
              <div className="register-input-with-icon mt-1">
                <img
                  src={`${process.env.PUBLIC_URL}/SVGIcons/Email.svg`}
                  alt="Mail"
                  className="register-input-icon"
                />
                <input
                  id="resendEmail"
                  type="email"
                  value={resendEmail}
                  onChange={e => { setResendEmail(e.target.value); setResendError(''); setResendInfo(''); }}
                  placeholder="Enter your email"
                  className={`register-input email-input w-full ${resendError ? 'input-error-border' : ''}`}
                  disabled={isResending}
                />
              </div>
              {resendError && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {resendError}
                </p>
              )}
              {resendInfo && (
                <p className="text-sm mt-1" style={{ color: '#0F796B' }}>{resendInfo}</p>
              )}
            </div>

            <div className="flex flex-col gap-3 mt-4">
              <button
                onClick={handleResend}
                disabled={isResending}
                className="add-device-save-btn"
              >
                {isResending ? 'Sending…' : 'Resend Verification Email'}
              </button>
              <button
                onClick={() => navigate('/login')}
                className="add-device-cancel-btn"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
